import { useSuspenseQuery } from '@apollo/client/react';
import { SheetTitleSection } from '@optools/app/components/sheet/sheet-title-section';
import { ErrorAlert } from '@optools/components/alerts/error';
import { ScrollPage } from '@optools/components/layout/layout';
import { Button } from '@optools/components/ui/button';
import { ESNI_RULES } from '@optools/services/operator-gateway/esni/queries';
import { UpdateEsniRule } from '@optools/tools/esni/components/rules/update-esni-rule';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

export const Route = createFileRoute('/_auth/esni/rules/$ruleId')({
  component: RouteComponent,
});

export function RouteComponent() {
  const { ruleId } = useParams({ from: '/_auth/esni/rules/$ruleId' });
  const navigate = useNavigate({ from: Route.fullPath });

  const {
    data: { esniRules },
    error,
    refetch,
  } = useSuspenseQuery(ESNI_RULES, {
    context: { skipErrorNotification: true },
  });

  const rule = esniRules.find((r) => r.id === ruleId);

  if (error) {
    return <ErrorAlert error={error} title="ESNI Rules Error" onRetry={refetch} />;
  }

  if (!rule) {
    return (
      <ScrollPage>
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          <p className="text-sm">No rule exists for this ID.</p>
          <Button asChild variant="secondary">
            <Link to="/esni/rules">Back to rules</Link>
          </Button>
        </div>
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <div className="mx-auto grid max-w-2xl gap-6">
        <div className="flex flex-col gap-2">
          <Button asChild className="-ml-2 w-fit" variant="ghost">
            <Link to="/esni/rules">
              <ArrowLeft className="mr-2 size-4" />
              Back to rules
            </Link>
          </Button>
          <h1 className="text-lg font-semibold">ESNI Rule</h1>
          <p className="sr-only">View or edit existing ESNI rule to control asset access</p>
          <SheetTitleSection className="mt-2" id={rule.id} title="Rule ID">
            {rule.id}
          </SheetTitleSection>
        </div>
        <UpdateEsniRule
          rule={rule}
          onReset={() => {
            navigate({ to: '/esni/rules' });
          }}
        />
      </div>
    </ScrollPage>
  );
}
